import {
  computed,
  ref,
  shallowReactive,
  type ComputedRef,
  type MaybeRef,
  type Ref,
  shallowRef,
  watch
} from 'vue'
import type { DataTableRowInternal } from '../types'
import { useEffect } from '@/utils/reactive/useEffect'
import type { Paginator } from '@/types/pagination'
import { uref } from '@/utils/reactive/uref'
import { throttledRef } from '@vueuse/core'

export type DataTableRowState = {
  id: string
  selected: boolean
}

export type UseDataTableStateProps = {
  rows?: MaybeRef<MaybeRef<DataTableRowInternal>[]>
  paginator?: MaybeRef<Paginator | undefined>
}

type SortSlot = {
  key: string
  direction: number
}
type SortState = Record<string, Ref<SortSlot>>
type UnwrappedSortState = Record<string, number>

type State = {
  rows: Record<string, Ref<DataTableRowState>>
  allSelected: boolean
}

export type UseDataTableState = {
  rows: ComputedRef<Record<string, Ref<DataTableRowState>>>
  rowIds: ComputedRef<string[]>
  setRows: (rows: DataTableRowInternal[]) => void
  allSelected: ComputedRef<boolean>
  setAllSelected: (selected: boolean) => void
  selectionCount: ComputedRef<number>
  selectedIds: ComputedRef<string[]>
  queryString: Ref<string>
  setQueryString: (value: string) => void
  setSortDirection: (key: string, direction: number) => void
  toggleSortDirection: (key: string) => number
  getSortSlot: (key: string) => Ref<SortSlot> | null
  sorting: Ref<UnwrappedSortState>
}

export const useDataTableState = (props: UseDataTableStateProps): UseDataTableState => {
  const state = shallowReactive<State>({
    rows: {},
    allSelected: false
  })
  const sortCounter = ref<number>(0)
  const sortState = shallowRef<SortState>({})

  const unwrappedSorting = ref<UnwrappedSortState>({})

  watch(
    sortCounter,
    () => {
      unwrappedSorting.value = Object.assign(
        {},
        ...Object.entries(sortState.value)
          .filter(([_key, value]) => value.value.direction !== 0)
          .map(([key, slot]) => {
            return {
              [slot.value.key || key]: slot.value.direction
            }
          })
      )
    },
    { immediate: false }
  )

  const queryString = ref<string>('')
  const updateSorting = () => {
    sortCounter.value = sortCounter.value + 1
  }

  const setQueryString = (value: string) => {
    queryString.value = value
  }

  const upsertSortSlot = (key: string, slot: SortSlot, update: boolean = false): Ref<SortSlot> => {
    if (sortState.value[key]) {
      if (update) {
        sortState.value[key].value = slot
      }
      return sortState.value[key]
    }
    sortState.value[key] = ref<SortSlot>(slot)
    return sortState.value[key]
  }

  const getSortSlot = (key: string): Ref<SortSlot> | null => {
    return sortState.value[key] || null
  }

  const setSortDirection = (key: string, direction: number) => {
    const slot = getSortSlot(key)
    if (!slot) return
    slot.value.direction = direction
    updateSorting()
  }

  const resetSorting = (except: string) => {
    Object.keys(sortState.value).filter(it => it !== except).forEach((key) => {
      if (sortState.value[key]) {
        sortState.value[key].value.direction = 0;
      }
    })
  }
  
  const toggleSortDirection = (key: string) => {
    resetSorting(key);
    
    const slot = getSortSlot(key)
    if (!slot) return 0
    const state = slot.value
    const nextState = state

    if (state.direction <= -1) {
      nextState.direction = 0
    } else if (Math.abs(nextState.direction) === 0) {
      nextState.direction = 1
    } else if (nextState.direction >= 1) {
      nextState.direction = -1
    }

    slot.value = nextState
    sortState.value = {
      ...sortState.value,
      [slot.value.key || key]: slot
    }
    updateSorting()
    return nextState.direction
  }

  const createRefs = (rows: DataTableRowInternal[]): Record<string, Ref<DataTableRowState>> => {
    return Object.assign(
      {},
      ...rows.map((row): Record<string, Ref<DataTableRowState>> => {
        const id = row.id
        return {
          [id]: ref<DataTableRowState>({
            id,
            selected: false
          })
        }
      })
    )
  }

  const receiveRows = (rows: DataTableRowInternal[]) => {
    const refs = createRefs(rows)
    state.rows = refs

    if (rows.length > 0 && rows[0].columns && rows[0].columns.length > 0) {
      const sample = rows[0]

      const slots: Record<string, SortSlot> = Object.assign(
        {} as Record<string, SortSlot>,
        ...sample.columns.map((col, i): Record<string, SortSlot> => {
          const slotKey = col.value.sortKey || col.value.key || col.value.label || `${i}`
          const sortKey = col.value.sortKey || slotKey
          return {
            [slotKey]: {
              key: sortKey,
              direction: 0
            }
          }
        })
      )

      sortState.value = Object.assign(
        {},
        ...Object.entries(slots).map(([key, slot]) => {
          return {
            [slot.key || key]: upsertSortSlot(slot.key || key, slot)
          }
        })
      )
    }
  }

  if (props.rows) {
    useEffect(() => {
      if (!props.rows) return
      receiveRows(uref(props.rows).map((it) => uref(it)))
    }, [() => uref(props.rows)])
  }

  const setRows = (rows: DataTableRowInternal[]) => {
    receiveRows(rows)
  }

  const rows = computed(() => state.rows)
  const allSelected = computed(() => state.allSelected)

  const setAllSelected = (selected: boolean) => {
    state.allSelected = selected

    const refs = Object.values(rows.value)
    refs.forEach((r) => {
      r.value.selected = selected
    })
  }

  const selectionCount = computed(
    () => Object.values(rows.value).filter((it) => it.value.selected === true).length
  )

  const selectedIds = computed(() =>
    Object.values(rows.value)
      .filter((it) => it.value.selected === true)
      .map((it) => it.value.id)
  )

  const throttledQueryString = throttledRef<string>(queryString, 160)
  const rowIds = computed(() => Object.values(rows.value).map((it) => it.value.id))

  return {
    rows,
    rowIds,
    setRows,
    allSelected,
    setAllSelected,
    selectionCount,
    selectedIds,

    queryString: throttledQueryString,
    setQueryString,

    sorting: unwrappedSorting,
    setSortDirection,
    toggleSortDirection,
    getSortSlot
  }
}
